import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Mail, MapPin, Linkedin, Github, BookOpen, Gamepad, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../i18n/translations';
// ⚠️ Replace this with your own Web3Forms access key
// Get one for free at: https://web3forms.com (enter your email → get key)
const WEB3FORMS_ACCESS_KEY = 'de7be81f-d8ec-435e-a8b6-49f267699bc5';
type FormStatus = 'idle' | 'sending' | 'success' | 'error';
export function ContactSection() {
  const {
    language
  } = useLanguage();
  const t = translations[language].contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: t.form.subjects.general,
    message: ''
  });
  const [status, setStatus] = useState<FormStatus>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          from_name: 'WarpWork Contact Form'
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const result = await response.json();
      if (result.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: t.form.subjects.general,
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };
  const resetForm = () => {
    setStatus('idle');
  };
  return <section id="contact" className="py-24 relative" data-id="element-71">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-72">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-73">
          {/* Contact Info */}
          <div data-id="element-74">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-75">{t.title}</h2>
            <p className="text-gray-400 text-lg mb-8" data-id="element-76">{t.description}</p>

            <div className="space-y-6 mb-12" data-id="element-77">
              <div className="flex items-center space-x-4" data-id="element-78">
                <div className="p-3 bg-white/5 rounded-full text-[#00c9a7]" data-id="element-79">
                  <Mail size={24} data-id="element-80" />
                </div>
                <div data-id="element-81">
                  <div className="text-sm text-gray-500" data-id="element-82">{t.email}</div>
                  <div className="text-white font-medium" data-id="element-83">
                    julien.machado@warpwork.fr
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4" data-id="element-84">
                <div className="p-3 bg-white/5 rounded-full text-[#1a4fd6]" data-id="element-85">
                  <MapPin size={24} data-id="element-86" />
                </div>
                <div data-id="element-87">
                  <div className="text-sm text-gray-500" data-id="element-88">{t.location}</div>
                  <div className="text-white font-medium" data-id="element-89">
                    {t.locationValue}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-4" data-id="element-90">
              <a href="https://github.com/Saturn1B" className="p-3 bg-white/5 rounded-full hover:bg-[#00f5a0]/20 hover:text-[#00f5a0] transition-colors" title="GitHub" data-id="element-91">
                <Github size={24} data-id="element-92" />
              </a>
              <a href="https://www.linkedin.com/in/julien-machado-6a20731ab/" className="p-3 bg-white/5 rounded-full hover:bg-[#1a4fd6]/20 hover:text-[#1a4fd6] transition-colors" title="LinkedIn" data-id="element-93">
                <Linkedin size={24} data-id="element-94" />
              </a>
              <a href="https://phobosrandom.itch.io/" target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-full hover:bg-[#fa5c5c]/20 hover:text-[#fa5c5c] transition-colors" title="itch.io" data-id="element-95">
                <Gamepad size={24} data-id="element-96" />
              </a>
              <a href="https://julienmachado.framer.website" target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 rounded-full hover:bg-[#00c9a7]/20 hover:text-[#00c9a7] transition-colors" title="Portfolio" data-id="element-97">
                <BookOpen size={24} data-id="element-98" />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 relative overflow-hidden" data-id="element-99">
            {/* Success State */}
            {status === 'success' && <div className="absolute inset-0 bg-[#050510]/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-8 animate-fadeIn" data-id="element-100">
                <div className="w-20 h-20 rounded-full bg-[#00f5a0]/10 border border-[#00f5a0]/30 flex items-center justify-center mb-6 animate-scaleIn" data-id="element-101">
                  <CheckCircle2 size={40} className="text-[#00f5a0]" data-id="element-102" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 text-center" data-id="element-103">
                  {t.form.successTitle}
                </h3>
                <p className="text-gray-400 text-center max-w-sm mb-8" data-id="element-104">
                  {t.form.successDescription}
                </p>
                <button onClick={resetForm} className="text-[#00c9a7] hover:text-[#00f5a0] transition-colors text-sm font-medium underline underline-offset-4" data-id="element-105">
                  {t.form.sendAnother}
                </button>
              </div>}

            {/* Error State */}
            {status === 'error' && <div className="absolute inset-0 bg-[#050510]/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-8 animate-fadeIn" data-id="element-106">
                <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6 animate-scaleIn" data-id="element-107">
                  <AlertCircle size={40} className="text-red-400" data-id="element-108" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 text-center" data-id="element-109">
                  {t.form.errorTitle}
                </h3>
                <p className="text-gray-400 text-center max-w-sm mb-8" data-id="element-110">
                  {t.form.errorDescription}
                </p>
                <button onClick={resetForm} className="text-[#00c9a7] hover:text-[#00f5a0] transition-colors text-sm font-medium underline underline-offset-4" data-id="element-111">
                  {t.form.sendAnother}
                </button>
              </div>}

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-112">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-113">
                <div className="space-y-2" data-id="element-114">
                  <label htmlFor="name" className="text-sm font-medium text-gray-300" data-id="element-115">
                    {t.form.name}
                  </label>
                  <input type="text" id="name" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} disabled={status === 'sending'} className="w-full bg-[#050510] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c9a7] focus:ring-1 focus:ring-[#00c9a7] transition-colors disabled:opacity-50" placeholder={t.form.namePlaceholder} data-id="element-116" />
                </div>
                <div className="space-y-2" data-id="element-117">
                  <label htmlFor="email" className="text-sm font-medium text-gray-300" data-id="element-118">
                    {t.form.email}
                  </label>
                  <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} disabled={status === 'sending'} className="w-full bg-[#050510] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c9a7] focus:ring-1 focus:ring-[#00c9a7] transition-colors disabled:opacity-50" placeholder={t.form.emailPlaceholder} data-id="element-119" />
                </div>
              </div>

              <div className="space-y-2" data-id="element-120">
                <label htmlFor="subject" className="text-sm font-medium text-gray-300" data-id="element-121">
                  {t.form.subject}
                </label>
                <select id="subject" value={formData.subject} onChange={e => setFormData({
                ...formData,
                subject: e.target.value
              })} disabled={status === 'sending'} className="w-full bg-[#050510] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c9a7] focus:ring-1 focus:ring-[#00c9a7] transition-colors disabled:opacity-50" data-id="element-122">
                  <option value={t.form.subjects.general} data-id="element-123">
                    {t.form.subjects.general}
                  </option>
                  <option value={t.form.subjects.freelance} data-id="element-124">
                    {t.form.subjects.freelance}
                  </option>
                  <option value={t.form.subjects.job} data-id="element-125">
                    {t.form.subjects.job}
                  </option>
                  <option value={t.form.subjects.other} data-id="element-126">
                    {t.form.subjects.other}
                  </option>
                </select>
              </div>

              <div className="space-y-2" data-id="element-127">
                <label htmlFor="message" className="text-sm font-medium text-gray-300" data-id="element-128">
                  {t.form.message}
                </label>
                <textarea id="message" rows={4} required value={formData.message} onChange={e => setFormData({
                ...formData,
                message: e.target.value
              })} disabled={status === 'sending'} className="w-full bg-[#050510] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c9a7] focus:ring-1 focus:ring-[#00c9a7] transition-colors disabled:opacity-50" placeholder={t.form.messagePlaceholder} data-id="element-129"></textarea>
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={status === 'sending'} data-id="element-130">
                {status === 'sending' ? <span className="flex items-center gap-2" data-id="element-131">
                    <Loader2 size={18} className="animate-spin" data-id="element-132" />
                    {t.form.sending}
                  </span> : t.form.send}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-24 pt-8 border-t border-white/10 text-center text-gray-500 text-sm" data-id="element-133">
          <p data-id="element-134">
            {t.footer.replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </div>
    </section>;
}